import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * SuccessToastComponent displays a transient success notification with a title, message, icon, and an animated hide/close behavior.
 * @component
 * @selector app-success-toast
 * @example
 *   <app-success-toast [show]="true" [title]="'Saved!'" [message]="'Your changes were saved.'" [type]="'checkin'" (close)="onToastClose()"></app-success-toast>
 * @input {boolean} show - Controls visibility of the toast. When toggled from true to false the component plays a hide animation and emits the close event. Default: false.
 * @input {string} title - Title text displayed in the toast. Default: 'Checked In!'.
 * @input {string} message - Message body displayed under the title. Default: "You're now clocked in and ready.".
 * @input {'checkin'|'checkout'} type - Visual/semantic variant of the toast; currently supports 'checkin' or 'checkout'. Default: 'checkin'.
 * @output {EventEmitter<void>} close - Emitted after the hide animation completes (either when programmatically hidden via the show input or when the user clicks the close icon).
 * Key methods:
 *   - ngOnChanges(changes: SimpleChanges): Watches the show input; when show transitions from true to false it triggers the hide animation, resets internal state after the animation delay, and emits the close event.
 *   - onClose(): Initiates the hide animation in response to the user's close action, then emits the close event after the animation completes.
 * Lifecycle hooks:
 *   - OnChanges: Used to detect changes to inputs (notably show) to control animation timing and event emission.
 */
@Component({
  selector: 'app-success-toast',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div
      class="success-toast"
      [class.show]="show && !isHiding"
      [class.hide]="isHiding"
      *ngIf="show || isHiding"
    >
      <div class="toast-content">
        <div class="border-left"></div>
        <div class="toast-body">
          <div class="icon">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M16.5 5.5L8 14L3.5 9.5"
                stroke="#079271"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div class="message-content">
            <div class="title">{{ title }}</div>
            <div class="message">{{ message }}</div>
          </div>
          <div class="close-icon" (click)="onClose()">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 4L4 12M4 4L12 12"
                stroke="#079271"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./success-toast.component.scss'],
})
export class SuccessToastComponent implements OnChanges {
  @Input() show = false;
  @Input() title = 'Checked In!';
  @Input() message = "You're now clocked in and ready.";
  @Input() type: 'checkin' | 'checkout' = 'checkin';
  @Output() close = new EventEmitter<void>();

  isHiding = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['show'] && !changes['show'].currentValue && changes['show'].previousValue) {
      // Toast is being hidden, trigger hide animation
      this.isHiding = true;
      setTimeout(() => {
        this.isHiding = false;
        this.close.emit();
      }, 300); // Match the animation duration
    }
  }

  onClose(): void {
    this.isHiding = true;
    setTimeout(() => {
      this.isHiding = false;
      this.close.emit();
    }, 300); // Match the animation duration
  }
}
